import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Calendar, User, Eye, ArrowLeft, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/blogs/${slug}`);
      setBlog(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching blog:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-[#2d2d2d]">Loading...</div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-light text-[#2d2d2d] mb-4">Blog Not Found</h1>
          <Link to="/blog">
            <Button className="bg-[#0A1A50] hover:bg-[#060F2E] text-white">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Blogs
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog.meta_title || blog.title} | Reagle</title>
        <meta name="description" content={blog.meta_description || blog.excerpt} />
        {blog.meta_keywords && <meta name="keywords" content={blog.meta_keywords} />}
        <meta property="og:title" content={blog.meta_title || blog.title} />
        <meta property="og:description" content={blog.meta_description || blog.excerpt} />
        {blog.featured_image && <meta property="og:image" content={blog.featured_image} />}
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.meta_title || blog.title} />
        <meta name="twitter:description" content={blog.meta_description || blog.excerpt} />
        {blog.featured_image && <meta name="twitter:image" content={blog.featured_image} />}
      </Helmet>

      <div className="min-h-screen pt-20 bg-white">
        {/* Hero Section */}
        <section className="bg-[#0A1A50] text-white py-20">
          <div className="max-w-4xl mx-auto px-6">
            <Link to="/blog" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Blogs
            </Link>
            
            {blog.category && (
              <span className="text-white/80 text-sm font-normal mb-3 block">
                {blog.category}
              </span>
            )}
            
            <h1
              className="text-4xl md:text-5xl font-light mb-6"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
              }}
            >
              {blog.title}
            </h1>
            
            <div className="flex items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(blog.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>{blog.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                <span>{blog.views} views</span>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        {blog.featured_image && (
          <section className="py-8 bg-gray-50">
            <div className="max-w-4xl mx-auto px-6">
              <img
                src={blog.featured_image}
                alt={blog.title}
                className="w-full rounded-lg shadow-xl"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s'
                }}
              />
            </div>
          </section>
        )}

        {/* Blog Content */}
        <article className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <div 
              className="prose prose-lg max-w-none"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.8s ease-out 0.3s, transform 0.8s ease-out 0.3s'
              }}
            >
              <div 
                className="text-[#2d2d2d] font-light leading-relaxed text-lg whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
            </div>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-3 flex-wrap">
                  <Tag className="w-5 h-5 text-[#0A1A50]" />
                  {blog.tags.map((tag, idx) => (
                    <span key={idx} className="px-3 py-1 bg-gray-100 text-[#2d2d2d] text-sm rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-12 bg-[#0A1A50] text-white p-8 rounded-lg text-center">
              <h3 className="text-2xl font-light mb-4">Interested in learning more?</h3>
              <p className="mb-6 text-white/90">Get in touch with our team to discuss your real estate needs</p>
              <Link to="/contact">
                <Button className="bg-white text-[#0A1A50] hover:bg-white/90">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </article>
      </div>
    </>
  );
};

export default BlogDetail;
